import { useEffect, useRef, useState } from 'react';
import { FiBell, FiLogOut, FiUser } from 'react-icons/fi';
import type { AppNotification, Session } from '../types';
import { cn } from '../lib/cn';

interface TopbarProps {
  session: Session;
  isAdmin: boolean;
  notifications: AppNotification[];
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onLogout: () => void;
}

export default function Topbar({ session, isAdmin, notifications, onMarkRead, onMarkAllRead, onLogout }: TopbarProps) {
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  return (
    <header className="z-20 flex items-center justify-between gap-3 bg-gradient-to-r from-slate-900 to-slate-800 px-4 py-2 shadow-lg">
      <div className="flex min-w-0 items-center gap-3">
        <span className="truncate text-sm font-semibold text-slate-100">Rede</span>
        <span className="hidden truncate text-xs text-slate-400 sm:inline">
          Mapa de ativação e infraestrutura
        </span>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        <span className="flex items-center gap-1.5 rounded-full border border-slate-600 bg-white/10 px-3 py-1.5 text-xs font-medium text-slate-200">
          <FiUser size={12} />
          <span className="hidden sm:inline">{session.username}</span>
        </span>

        {isAdmin && (
          <div className="relative" ref={notifRef}>
            <button
              className={cn(
                'relative flex h-9 w-9 items-center justify-center rounded-full border text-slate-200 transition-colors',
                notifications.length > 0 ? 'border-amber-400/60 bg-amber-400/10' : 'border-slate-600 bg-white/10 hover:bg-white/20'
              )}
              onClick={() => setNotifOpen((v) => !v)}
              title="Notificações"
            >
              <FiBell size={18} />
              {notifications.length > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {notifications.length > 9 ? '9+' : notifications.length}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute right-0 top-11 z-30 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                  <span className="text-sm font-semibold text-slate-700">Notificações ({notifications.length})</span>
                  {notifications.length > 0 && (
                    <button className="text-xs font-semibold text-blue-600 hover:underline" onClick={onMarkAllRead}>
                      Marcar todas lidas
                    </button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-slate-400">Nenhuma notificação nova</div>
                ) : (
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.map((n) => (
                      <div
                        key={n._id}
                        className="flex items-start gap-2 border-b border-slate-50 px-4 py-2.5 last:border-0 hover:bg-slate-50"
                      >
                        <span className="flex-1 text-sm text-slate-600">{n.message}</span>
                        <button
                          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50"
                          onClick={() => onMarkRead(n._id)}
                          title="Marcar como lida"
                        >
                          ✓
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Desconecta só do módulo Rede — a sessão do painel continua. */}
        <button
          className="flex items-center gap-1.5 rounded-full border border-red-500/60 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-200 transition-colors hover:bg-red-500/20"
          onClick={onLogout}
          title="Desconectar do módulo Rede (não sai do painel)"
        >
          <FiLogOut size={16} />
          <span className="hidden sm:inline">Desconectar</span>
        </button>
      </div>
    </header>
  );
}
